import {combine, forward, guard, merge, sample} from 'effector';

import {RestError, showRestError} from 'core/errors';
import {initFiltersTable, initSortingsTable} from 'core/hooks/grid2.0';
import {mapFilters, mapSortings} from 'utils/grid-mappers';

import {
    clearDataCacheFx,
    data$,
    dataUngrouped$,
    defaultFilters,
    defaultSortings,
    fetchDataFx,
    fetchDataUngroupedFx,
    filters$,
    filtersChanged,
    filtersReset,
    Gate,
    pageNumber$,
    pageSize$,
    sortings$,
    sortingsChanged,
    sortingsReset,
    stateReset,
    tableStateCacheChanged,
    tableStateChanged,
    CommonGate,
    UngroupedGate,
    gridTokensCache$,
    gridCacheReset,
    gridCacheChanged,
    fetchStorageLocationsFx,
    storageLocationsNames$,
    storageLocationsAddresses$,
} from './index';

pageNumber$.reset(
    filtersChanged,
    sortingsChanged,
    stateReset,
    CommonGate.close,
    UngroupedGate.close,
);
pageSize$.reset(stateReset, CommonGate.close, UngroupedGate.close);
filters$.reset(stateReset, CommonGate.close, UngroupedGate.close);
sortings$.reset(stateReset, CommonGate.close, UngroupedGate.close);

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

forward({
    from: [CommonGate.open, UngroupedGate.open],
    to: [fetchStorageLocationsFx],
});

storageLocationsNames$
    .on(fetchStorageLocationsFx.done, (state, {result}) => {
        const entries = Object.keys(result);
        const mappedResult = entries.map((name, index) => ({
            id: index.toString(),
            value: name,
            label: name,
            text: result[name],
        }));
        return mappedResult;
    })
    .reset(CommonGate.close, UngroupedGate.close);

storageLocationsAddresses$
    .on(fetchStorageLocationsFx.done, (state, {result}) => {
        const entries = Object.values(result);
        const addresses = Array.from(new Set(entries));
        const mappedResult = addresses.map((address, index) => ({
            id: index.toString(),
            value: address,
            label: address,
        }));
        return mappedResult;
    })
    .reset(CommonGate.close, UngroupedGate.close);

data$
    .on(fetchDataFx.doneData, (state, data) => data)
    .reset(Gate.close, CommonGate.close, UngroupedGate.close);
dataUngrouped$
    .on(fetchDataUngroupedFx.doneData, (state, data) => data)
    .reset(Gate.close, UngroupedGate.close, CommonGate.close);

gridTokensCache$
    .on(fetchDataUngroupedFx.done, (state, {params, result}) => ({
        ...state,
        [params.paging.pageNumber + 1]: result.nextPageRef ?? null,
    }))
    .on(gridCacheReset, () => ({0: null}))
    .on(gridCacheChanged, (state, {pageNumber, token}) => ({
        ...state,
        [pageNumber]: token,
    }));

forward({
    from: tableStateCacheChanged,
    to: clearDataCacheFx,
});

sample({
    clock: CommonGate.open,
    target: clearDataCacheFx,
});

sample({
    clock: UngroupedGate.open,
    target: clearDataCacheFx,
});

const commonTriggers = merge([CommonGate.open, tableStateChanged]);
const commonAllowed = guard({
    source: commonTriggers,
    filter: CommonGate.status,
});
sample({
    clock: commonAllowed,
    source: combine({
        sortings: sortings$,
        pageNumber: pageNumber$,
        pageSize: pageSize$,
        filters: filters$,
    }),
    fn: ({pageNumber, pageSize, filters, sortings}) => ({
        paging: {
            pageNumber,
            pageSize,
        },
        filters: Object.entries(filters).map(([key, value]) => ({
            id: key,
            value: typeof value === 'object' && 'value' in value ? value.value : value,
        })),
        sortings: mapSortings(sortings),
    }),
    target: fetchDataFx,
});

const ungroupedTriggers = merge([UngroupedGate.open, tableStateChanged]);
const ungroupedAllowed = guard({
    source: ungroupedTriggers,
    filter: UngroupedGate.status,
});
sample({
    clock: ungroupedAllowed,
    source: combine({
        sortings: sortings$,
        pageNumber: pageNumber$,
        pageSize: pageSize$,
        filters: filters$,
        cache: gridTokensCache$,
    }),
    fn: ({pageNumber, pageSize, filters, sortings, cache}) => ({
        paging: {
            pageNumber,
            pageSize,
        },
        filters: mapFilters(filters),
        sortings: mapSortings(sortings),
        pageRef: cache?.[pageNumber] ?? null,
    }),
    target: fetchDataUngroupedFx,
});

fetchDataFx.fail.watch(({error}) => showRestError(error as RestError));
fetchDataUngroupedFx.fail.watch(({error}) => showRestError(error as RestError));
